import { useRef, useState } from "react";
import { Camera, Image as ImageIcon, Loader2, X, Sparkles, UtensilsCrossed, Coffee, PackageOpen, Plus } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import MealCard from "../components/MealCard";

const KIND_OPTIONS = [
  { id: "meal",    label: "Meal",     Icon: UtensilsCrossed },
  { id: "drink",   label: "Drink",    Icon: Coffee },
  { id: "package", label: "Packaged", Icon: PackageOpen },
];

const MAX_PHOTOS = 3;

export default function LogMeal() {
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const [kind, setKind] = useState("meal");
  const [photos, setPhotos] = useState([]); // [{ file, preview }]
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;
    setResult(null);
    setPhotos((prev) => {
      const next = [...prev];
      for (const f of incoming) {
        if (next.length >= MAX_PHOTOS) {
          toast.error(`Up to ${MAX_PHOTOS} photos per log`);
          break;
        }
        if (!/\.(jpe?g|png|webp)$/i.test(f.name) && !/(jpeg|png|webp)/.test(f.type)) {
          toast.error("Please use JPEG, PNG, or WEBP");
          continue;
        }
        next.push({ file: f, preview: URL.createObjectURL(f) });
      }
      return next;
    });
  };

  const removeAt = (i) => {
    setPhotos((prev) => {
      const p = prev[i];
      if (p) URL.revokeObjectURL(p.preview);
      return prev.filter((_, idx) => idx !== i);
    });
  };

  const clear = () => {
    photos.forEach((p) => URL.revokeObjectURL(p.preview));
    setPhotos([]);
    setNote("");
    setResult(null);
  };

  const analyze = async () => {
    if (!photos.length) { toast.error("Add a photo first"); return; }
    setLoading(true);
    try {
      const data = await api.analyze({ files: photos.map((p) => p.file), note, kind });
      setResult(data);
      toast.success("Analyzed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Analysis failed");
    } finally {
      setLoading(false);
    }
  };

  const placeholders = {
    meal:    'e.g. "Chicken biryani", "Lunch from Grab", "Extra rice"',
    drink:   'e.g. "Iced caramel latte large", "Coke 330ml", "Beer pint"',
    package: 'e.g. "Lays Classic 50g", "Cliff Bar peanut butter"',
  };

  return (
    <div className="px-5 pt-8 pb-6 space-y-6" data-testid="log-meal-page">
      <header>
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-sage">Log</p>
        <h1 className="font-heading text-3xl font-extrabold text-charcoal mt-2">What did you have?</h1>
        <p className="text-slate2 mt-2 text-sm leading-relaxed">
          Snap or upload a photo. Add the order screenshot or label too for a tighter estimate.
        </p>
      </header>

      {/* Kind toggle */}
      <div className="grid grid-cols-3 gap-2 p-1.5 bg-sand rounded-2xl" data-testid="kind-toggle">
        {KIND_OPTIONS.map(({ id, label, Icon }) => (
          <button
            key={id}
            onClick={() => { setKind(id); setResult(null); }}
            data-testid={`kind-${id}`}
            className={`flex items-center justify-center gap-2 py-2 rounded-xl text-sm font-bold tap-shrink transition-colors ${
              kind === id ? "bg-charcoal text-cream" : "text-slate2"
            }`}
          >
            <Icon size={16} strokeWidth={1.8} />
            <span className="hidden xs:inline">{label}</span>
            <span className="xs:hidden text-xs">{label}</span>
          </button>
        ))}
      </div>

      {!result && (
        <>
          {/* Selected photo thumbnails */}
          {photos.length > 0 && (
            <div className="grid grid-cols-3 gap-2" data-testid="photo-previews">
              {photos.map((p, i) => (
                <div key={i} className="relative">
                  <img src={p.preview} alt={`Selected ${i + 1}`} className="w-full h-24 object-cover rounded-2xl" />
                  <button
                    onClick={() => removeAt(i)}
                    data-testid={`remove-photo-${i}`}
                    className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-charcoal/70 text-cream flex items-center justify-center tap-shrink"
                    aria-label="Remove photo"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Add photo actions */}
          {photos.length < MAX_PHOTOS && (
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => cameraRef.current?.click()}
                data-testid="open-camera-btn"
                className="card-soft p-6 flex flex-col items-center gap-3 tap-shrink hover:shadow-floating transition-shadow"
              >
                <div className="w-12 h-12 rounded-2xl bg-sage-light flex items-center justify-center">
                  <Camera size={22} className="text-sage" />
                </div>
                <p className="font-heading font-bold text-charcoal">{photos.length ? "Add photo" : "Take photo"}</p>
                <p className="text-xs text-slate2 text-center">
                  {kind === "package" ? "Snap the label or front of pack" : "Use your camera"}
                </p>
              </button>
              <button
                onClick={() => fileRef.current?.click()}
                data-testid="open-upload-btn"
                className="card-soft p-6 flex flex-col items-center gap-3 tap-shrink hover:shadow-floating transition-shadow"
              >
                <div className="w-12 h-12 rounded-2xl bg-ochre-light flex items-center justify-center">
                  <ImageIcon size={22} className="text-ochre" />
                </div>
                <p className="font-heading font-bold text-charcoal">Upload</p>
                <p className="text-xs text-slate2 text-center">From gallery</p>
              </button>
              <input ref={cameraRef} type="file" accept="image/*" capture="environment" hidden onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} data-testid="camera-input" />
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" multiple hidden onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} data-testid="file-input" />
            </div>
          )}

          {photos.length > 0 && (
            <div className="card-soft p-5 space-y-4">
              <p className="text-xs text-slate2 flex items-center gap-1.5">
                <Plus size={13} /> {photos.length} of {MAX_PHOTOS} photos — add the order screenshot or label for a tighter range.
              </p>
              <label className="block">
                <span className="text-xs uppercase tracking-[0.15em] font-bold text-sage">Notes (optional)</span>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder={placeholders[kind]}
                  rows={2}
                  data-testid="note-input"
                  className="mt-2 w-full rounded-2xl bg-sand p-3 text-sm text-charcoal placeholder:text-slate2/60 focus:outline-none focus:ring-2 focus:ring-sage resize-none"
                />
              </label>
              <div className="flex gap-2">
                <button
                  onClick={clear}
                  data-testid="clear-photo"
                  className="rounded-2xl bg-sand px-4 py-3 font-heading font-bold text-charcoal tap-shrink"
                >
                  Clear
                </button>
                <button
                  onClick={analyze}
                  disabled={loading}
                  data-testid="analyze-btn"
                  className="flex-1 rounded-2xl bg-charcoal text-cream py-3 font-heading font-bold tap-shrink flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {loading ? <Loader2 className="animate-spin" size={18} /> : <Sparkles size={18} />}
                  {loading ? "Analyzing…" : `Analyze ${kind === "drink" ? "drink" : kind === "package" ? "package" : "meal"}`}
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {result && (
        <div className="space-y-4">
          <MealCard meal={result} />
          <button
            onClick={clear}
            data-testid="log-another-btn"
            className="w-full rounded-2xl bg-sand py-3 font-heading font-bold text-charcoal tap-shrink"
          >
            Log another
          </button>
        </div>
      )}
    </div>
  );
}
